import React from "react";
import { Box } from "@mui/material";
import { DescriptionOutlined } from "@mui/icons-material";
import { dt } from "../../themes/redesignTokens";

// ═══════════════════════════════════════════════════════════════
//  ChatListRefChip (MC-25)
//  Gold-tinted pill chip that renders an inline "List NNN" reference
//  parsed from a bot message's <list-ref> tag. Sibling of
//  ChatSrCitationChip — same token palette, different join semantics
//  (this one opens an artifact by `page`, not a service report by id).
//
//  Three visual states: default / viewed / active (tab currently open).
//  Click behavior is delegated to the parent (onClick) which is expected
//  to dispatch `openArtifactAtPage({ page })`.
// ═══════════════════════════════════════════════════════════════

const GOLD = "#D4A017";
const GOLD_SOFT_BG = "rgba(212, 160, 23, 0.06)";        // #D4A0170F — matches Paper "default"
const GOLD_VIEWED_BG = "rgba(212, 160, 23, 0.15)";      // #D4A01726 — matches Paper "hover/focus"
const GOLD_BORDER = "rgba(212, 160, 23, 0.35)";         // #D4A01759
const GOLD_BORDER_STRONG = "rgba(212, 160, 23, 0.55)";  // #D4A0178C

export interface ChatListRefChipProps {
  /** Supplementary display/debug ID (e.g. "050"). Not a join key. */
  listId: string;
  /** Canonical join key — `page` from the tag. */
  page: number;
  /** Optional name from the tag's `name` attribute. */
  name?: string;
  /** Visible label (text between the tags, e.g. "List 050"). */
  label: string;
  /** True when the referenced page's tab is currently open. */
  isActive?: boolean;
  /** True when this chip was clicked at least once this session. */
  isViewed?: boolean;
  /** Click handler — parent dispatches openArtifactAtPage. */
  onClick?: (payload: { listId: string; page: number; name?: string }) => void;
}

const ChatListRefChip: React.FC<ChatListRefChipProps> = ({
  listId,
  page,
  name,
  label,
  isActive = false,
  isViewed = false,
  onClick,
}) => {
  const clickable = !!onClick;

  let bg = GOLD_SOFT_BG;
  let border = GOLD_BORDER;
  let labelColor = dt.textPrimary;
  let iconColor = GOLD;
  if (isActive) {
    bg = GOLD;
    border = GOLD;
    labelColor = "#FFFFFF";
    iconColor = "#FFFFFF";
  } else if (isViewed) {
    bg = GOLD_VIEWED_BG;
  }

  const handleClick = () => {
    if (!clickable) return;
    onClick?.({ listId, page, name });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.({ listId, page, name });
    }
  };

  const ariaLabel = name ? `${label} (${name}) — page ${page}` : `${label} — page ${page}`;

  return (
    <Box
      component="span"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      aria-label={ariaLabel}
      aria-disabled={!clickable}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: "22px",
        padding: "0 10px",
        borderRadius: "999px",
        backgroundColor: bg,
        border: `1px solid ${border}`,
        cursor: clickable ? "pointer" : "default",
        transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
        userSelect: "none",
        verticalAlign: "baseline",
        // Keep inline with surrounding prose
        mx: "2px",
        "&:hover": clickable && !isActive
          ? {
              borderColor: GOLD_BORDER_STRONG,
              backgroundColor: GOLD_VIEWED_BG,
              boxShadow: "0 2px 6px rgba(212, 160, 23, 0.2)",
            }
          : undefined,
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 2px rgba(212, 160, 23, 0.35)`,
        },
      }}
    >
      <DescriptionOutlined sx={{ fontSize: "11px", color: iconColor }} />
      <Box
        component="span"
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "11px",
          fontWeight: 600,
          color: labelColor,
          letterSpacing: "0.02em",
          lineHeight: 1,
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

export default ChatListRefChip;
